/*
 * Geluid op de tablet, gemaakt met Web Audio: geen bestanden, dus niets te
 * laden en het werkt ook offline. iOS laat pas geluid toe na een tik of een
 * toets; daarom hangt ontgrendel() aan de eerste aanraking (de screensaver
 * wegtikken telt ook). De stijl kiest een medewerker in het menu, per tablet.
 */

export type Stijl = 'uit' | 'kassa' | 'ping' | 'akkoord' | 'toeter'
export type Moment = 'punt' | 'wacht' | 'fout' | 'ingewisseld'

export const STIJLEN: { k: Stijl; naam: string }[] = [
  { k: 'kassa', naam: 'Kassa' },
  { k: 'ping', naam: 'Ping' },
  { k: 'akkoord', naam: 'Akkoord' },
  { k: 'toeter', naam: 'Toeter' },
  { k: 'uit', naam: 'Uit' },
]

const SLEUTEL = 'tp-tablet-geluid'

export function stijl(): Stijl {
  try {
    const s = localStorage.getItem(SLEUTEL)
    return STIJLEN.some((x) => x.k === s) ? s as Stijl : 'kassa'
  } catch { return 'kassa' }
}

export function zetStijl(s: Stijl) {
  try { localStorage.setItem(SLEUTEL, s) } catch { /* privévenster */ }
}

let ctx: AudioContext | null = null

function context(): AudioContext | null {
  if (ctx && ctx.state !== 'closed') return ctx
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AC) return null
  ctx = new AC()
  return ctx
}

/**
 * Aanroepen bij een aanraking of toets. iOS geeft het geluid pas vrij als er
 * binnen die aanraking echt iets speelt; een stil fragment van één sample is
 * genoeg. resume() is in Safari asynchroon: daarom wacht speel() er later op.
 */
export function ontgrendel() {
  try {
    /* Safari 17+: als media afspelen, dus ook hoorbaar met de stil-schakelaar aan. */
    const nav = navigator as Navigator & { audioSession?: { type: string } }
    if (nav.audioSession && nav.audioSession.type !== 'playback') nav.audioSession.type = 'playback'
    const c = context()
    if (!c) return
    if (c.state !== 'running') {
      const stil = c.createBufferSource()
      stil.buffer = c.createBuffer(1, 1, 22050)
      stil.connect(c.destination)
      stil.start(0)
      void c.resume().catch(() => { /* volgende aanraking opnieuw */ })
    }
  } catch { /* geen geluid, geen probleem */ }
}

interface Toon { hz: number; op: number; duur: number; vorm?: OscillatorType; volume?: number; naar?: number; filter?: number }

function plan(c: AudioContext, tonen: Toon[]) {
  const t0 = c.currentTime + 0.03
  for (const t of tonen) {
    const o = c.createOscillator()
    const g = c.createGain()
    const start = t0 + t.op
    o.type = t.vorm ?? 'sine'
    o.frequency.setValueAtTime(t.hz, start)
    if (t.naar) o.frequency.exponentialRampToValueAtTime(t.naar, start + t.duur)
    g.gain.setValueAtTime(0.0001, start)
    g.gain.exponentialRampToValueAtTime(t.volume ?? 0.3, start + 0.012)
    g.gain.exponentialRampToValueAtTime(0.0001, start + t.duur)
    if (t.filter) {
      const f = c.createBiquadFilter()
      f.type = 'lowpass'
      f.frequency.value = t.filter
      o.connect(f).connect(g)
    } else o.connect(g)
    g.connect(c.destination)
    o.start(start)
    o.stop(start + t.duur + 0.03)
  }
}

function speelTonen(tonen: Toon[]) {
  try {
    ontgrendel()
    const c = context()
    if (!c) return
    if (c.state === 'running') { plan(c, tonen); return }
    /* Nog aan het opstarten (of onderbroken door iOS): even wachten, hooguit een seconde. */
    const klaar = c.resume().then(() => { if (c.state === 'running') plan(c, tonen) })
    void Promise.race([klaar, new Promise((r) => setTimeout(r, 1000))]).catch(() => { /* stil */ })
  } catch { /* stil */ }
}

/* Punt erbij: het geluid van de gekozen stijl. */
const PUNT: Record<Exclude<Stijl, 'uit'>, Toon[]> = {
  kassa: [
    { hz: 1319, op: 0, duur: 0.12, vorm: 'triangle', volume: 0.32 },
    { hz: 1760, op: 0.09, duur: 0.4, vorm: 'triangle', volume: 0.32 },
  ],
  ping: [
    { hz: 1568, op: 0, duur: 0.55, volume: 0.36 },
    { hz: 3136, op: 0, duur: 0.25, volume: 0.07 },
  ],
  akkoord: [
    { hz: 1047, op: 0, duur: 0.28, vorm: 'triangle', volume: 0.28 },
    { hz: 1319, op: 0.09, duur: 0.28, vorm: 'triangle', volume: 0.28 },
    { hz: 1568, op: 0.18, duur: 0.42, vorm: 'triangle', volume: 0.28 },
  ],
  toeter: [
    { hz: 233, op: 0, duur: 0.17, vorm: 'sawtooth', volume: 0.16, filter: 1300 },
    { hz: 294, op: 0, duur: 0.17, vorm: 'sawtooth', volume: 0.16, filter: 1300 },
    { hz: 233, op: 0.22, duur: 0.24, vorm: 'sawtooth', volume: 0.16, filter: 1300 },
    { hz: 294, op: 0.22, duur: 0.24, vorm: 'sawtooth', volume: 0.16, filter: 1300 },
  ],
}

/* De andere momenten klinken in elke stijl hetzelfde, zodat je ze leert herkennen. */
const VAST: Record<Exclude<Moment, 'punt'>, Toon[]> = {
  /* Al een punt gehad, of een nieuwe kaart: zacht en dalend, geen alarm. */
  wacht: [{ hz: 660, op: 0, duur: 0.32, volume: 0.22, naar: 520 }],
  /* Iets ging mis: twee lage, korte zoemers. */
  fout: [
    { hz: 196, op: 0, duur: 0.13, vorm: 'square', volume: 0.1, filter: 1500 },
    { hz: 196, op: 0.17, duur: 0.13, vorm: 'square', volume: 0.1, filter: 1500 },
  ],
  /* Beloning of voucher ingewisseld: een vrolijk loopje omhoog. */
  ingewisseld: [
    { hz: 1047, op: 0, duur: 0.22, vorm: 'triangle', volume: 0.26 },
    { hz: 1319, op: 0.08, duur: 0.22, vorm: 'triangle', volume: 0.26 },
    { hz: 1568, op: 0.16, duur: 0.22, vorm: 'triangle', volume: 0.26 },
    { hz: 2093, op: 0.24, duur: 0.5, vorm: 'triangle', volume: 0.26 },
  ],
}

export function speel(moment: Moment, s: Stijl = stijl()) {
  if (s === 'uit') return
  speelTonen(moment === 'punt' ? PUNT[s] : VAST[moment])
}
